import React, { useMemo, useState } from "react";
import dropdownIcon from "../../assets/componentsIcons/dropdown/dropdown.svg";
import editIcon from "../../assets/componentsIcons/dropdown/edit.svg";
import deleteIcon from "../../assets/componentsIcons/dropdown/delete.svg";
import unitIcon from "../../assets/componentsIcons/dropdown/unit.svg";
import notesIcon from "../../assets/componentsIcons/dropdown/doc.svg";
import unitsIcon from "../../assets/componentsIcons/listings/units.svg";
import vacanciesIcon from "../../assets/componentsIcons/listings/vacancies.svg";
import serviceRequestIcon from "../../assets/componentsIcons/listings/service-request.svg";
import { EntityType, NotesEntityTypesEnum, NoteService, PropertyItemVM } from "@propertelligent/client-api";
import ArrowOutgoingIcon from "../../assets/svgs/arrows/arrowOutGoingIcon";
import ArrowIncomingIcon from "../../assets/svgs/arrows/arrowIncomingIcon";
import ActionMenu from "../actionMenus/action-menu";
import { ConfirmationPopup } from "../confirmation-popup/confirmation-popup-demo";
import { useNavigate } from "react-router-dom";
import AddNotesForm from "../notes/AddNotesForm";
import AddUnitForm from "../unit/AddUnitForm";
import { GetImages } from "../../utils/GetImages";
import AddPaymentModal from "../payments/AddPaymentModal";
import CurrencyTooltip from "../../components/currencytooltip/CurrencyTooltip";
import { usePermissions } from "../../hooks/usePermissions";
import BluePayableIcon from "../../assets/Payment-Icon.svg";

interface PropertyCardItemProps {
  listingData: PropertyItemVM;
  IsFooter?: boolean;
  showConfirmation?: string | null;
  setShowConfirmation?: (id: string | null) => void;
  handleDeleteProperty?: (propertyId: any) => void;
  isDeletingProperty?: boolean;
  className?: string;
  onUnitAdded?: () => void;
  onPaymentAdded?: () => void; // Added optional prop for clarity
  disableNavigation?: boolean;
}

const PropertyCardItem: React.FC<PropertyCardItemProps> = ({
  listingData,
  IsFooter = true,
  showConfirmation,
  setShowConfirmation,
  handleDeleteProperty,
  isDeletingProperty = false,
  className: className = "",
  onUnitAdded,
  onPaymentAdded,
  disableNavigation = false,
}) => {
  const navigate = useNavigate();
  const { canWrite } = usePermissions();
  const [isModalOpen, setModalOpen] = useState(false);
  const [isUnitModalOpen, setUnitModalOpen] = useState(false);

  
  const [paymentModalConfig, setPaymentModalConfig] = useState({
    isOpen: false,
    entityId: null as number | null, 
    entityType: EntityType.Property 
  });

  const menuItems = useMemo(() => {
    const items = [];

    if (canWrite("Property")) {
      items.push({
        icon: editIcon,
        label: "Edit",
        action: () => navigate(`/properties/edit/${listingData.id}`),
        isDivider: false,
        isDestructive: false,
      });
      items.push({ isDivider: true });
      items.push({
        icon: deleteIcon,
        label: "Delete",
        action: (propertyId: string) => setShowConfirmation && setShowConfirmation(propertyId),
        isDivider: false,
        isDestructive: true,
      });
      items.push({ isDivider: true });
    }

    if (canWrite("Unit")) {
      items.push({
        icon: unitIcon,
        label: "Add Unit",
        action: () => setUnitModalOpen(true),
        isDivider: false,
        isDestructive: false,
      });
      items.push({ isDivider: true });
    }

    if (canWrite("Payments")) {
      items.push({
        icon: BluePayableIcon,
        label: "Add Payment",
        action: (Id) => {
          setPaymentModalConfig({
            isOpen: true,
            entityId: Number(Id),
            entityType: EntityType.Property,
          });
        },
        isDivider: false,
        isDestructive: false,
      });
      items.push({ isDivider: true });
    }

    if (canWrite("Property")) {
      items.push({
        icon: notesIcon,
        label: "Add Notes",
        action: () => setModalOpen(true),
        isDivider: false,
        isDestructive: false,
      });
    }

    // Remove any trailing dividers
    while (items.length > 0 && items[items.length - 1].isDivider) {
      items.pop();
    }

    return items;
  }, [canWrite, navigate, listingData.id, setShowConfirmation]);

  const footerData = [
    {
      iconSrc: unitsIcon,
      label: "Units",
      value: listingData.numberOfUnits,
    },
    {
      iconSrc: vacanciesIcon,
      label: "Vacancies",
      value: listingData.vacantUnits,
    },
    {
      iconSrc: serviceRequestIcon,
      label: "Open Service Requests",
      value: listingData.openServiceRequests,
    },
  ];

  return (
    <>

      <div key={listingData.id} className={`listing-card ${className}`}>
        {/* Top section */}
        <div
          className="listing-link"
          style={{
            textDecoration: "none",
            color: "inherit",
            cursor: disableNavigation ? "auto" : "pointer",
          }}
          onClick={() => {
            if (!disableNavigation) {
              navigate(`/properties/propertydetails/${listingData.id}`);
            }
          }}
        >
          <div className="listing-card-header">
            <div className="listing-image-container">
              <img
                src={
                  listingData?.mainImageBase64
                    ? `data:image/jpeg;base64,${listingData?.mainImageBase64}`
                    : GetImages.defaultPropertyIcon
                }
                alt={`Image of property at ${listingData?.streetAddress}`}
                width={listingData?.mainImageBase64 ? 56 : 20}
                height={listingData?.mainImageBase64 ? 56 : 20}
                className={`listing-image ${listingData?.mainImageBase64 ? "" : "listing-image-placeholder color-white-icon"}`}
              />
            </div>
            <div className="listing-details">
              <div className="listing-info-container">
                <div className="listing-info">
                  <h3 className="listing-address">
                    {listingData.streetAddress}
                  </h3>
                  <p className="listing-type">
                    {listingData.formatted.PropertyTypeId}
                  </p>
                </div>
                <div className="financial-metrics">
                  <div className="metric">
                    <p className="metric-value">{listingData.city}</p>
                    <p className="metric-label">City</p>
                  </div>
                  <div className="separator-vertical" />
                  <div className="metric">
                    <p className="metric-value">{listingData.state}</p>
                    <p className="metric-label">State</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {IsFooter && (
          <>
            <div className="separator-horizontal" />
            <div className="listing-card-footer">
              <div className="listing-stats">
                {footerData.map((stat, index) => (
                  <div className="stat" key={index}>
                    <img
                      src={stat.iconSrc}
                      alt={`${stat.label} icon`}
                      width={16}
                      height={16}
                      className="stat-icon"
                    />
                    <span className="stat-text">
                      {stat.label}: {stat.value}
                    </span>
                  </div>
                ))}
              </div>
              <div className="footer-actions">
                <div className="footer-highlights">
                  {listingData?.expensesDue > 0 && (
                    <div className="highlight-value negative">
                      <ArrowOutgoingIcon fill="currentColor" className="highlight-icon" />
                      <span className="highlight-text mx-2">
                        <CurrencyTooltip
                          content={listingData?.formatted.ExpensesDueRaw}
                          formatted={listingData?.formatted.ExpensesDue}
                        >
                          <p className="highlight-text mx-2">
                            {listingData.formatted.ExpensesDue}
                          </p>
                        </CurrencyTooltip>
                      </span>
                    </div>
                  )}

                  {listingData?.incomeDue > 0 && (
                    <div className="highlight-value positive">
                      <ArrowIncomingIcon fill="currentColor" className="highlight-icon" />
                      <span className="highlight-text mx-2">
                        <CurrencyTooltip
                          content={listingData?.formatted.IncomeDueRaw}
                          formatted={listingData?.formatted.IncomeDue}
                        >
                          <p className="highlight-text mx-2">
                            {listingData.formatted.IncomeDue}
                          </p>
                        </CurrencyTooltip>
                      </span>
                    </div>
                  )}
                </div>

                {menuItems.length > 0 && (
                  <ActionMenu
                    menuItems={menuItems.map((item) => ({
                      ...item,
                      action: () => item.action && item.action(String(listingData.id)),
                    }))}
                    trigger={
                      <button className="action-button" aria-label="More options">
                        <img
                          src={dropdownIcon}
                          alt="More options"
                          width={20}
                          height={20}
                          className="action-icon"
                        />
                      </button>
                    }
                  />
                )}
              </div>
            </div>
          </>
        )}

      </div>
      {isUnitModalOpen && (
        <AddUnitForm
          isModalOpen={isUnitModalOpen}
          setModalOpen={setUnitModalOpen}
          propertyId={listingData?.id}
          isProperty={true}
          onUnitAdded={onUnitAdded}
        />
      )}

      {isModalOpen && (
        <AddNotesForm
          entityId={listingData.id}
          entityTypeId={NotesEntityTypesEnum.Property}
          isModalOpen={isModalOpen}
          setModalOpen={setModalOpen}
        />
      )}

      {/* Add PAYMENT*/}
      {
        paymentModalConfig.isOpen && (
          <AddPaymentModal
            isOpen={paymentModalConfig.isOpen}
            onClose={() => {
              setPaymentModalConfig(prev => ({ ...prev, isOpen: false }));
              // Trigger refresh: utilize onPaymentAdded if available, otherwise onUnitAdded
              if (onPaymentAdded) {
                onPaymentAdded();
              } else if (onUnitAdded) {
                onUnitAdded();
              }
            }}
            entityIdProp={paymentModalConfig.entityId || undefined}
            entityTypeIdProp={paymentModalConfig.entityType}
            defaultPaymentMode="1"
          />
        )
      }


      {/* Confirmation Popup for Delete */}
      {showConfirmation === String(listingData.id) && (
        <ConfirmationPopup
          isOpen={true}
          title="Delete Property"
          message="Do you want to delete this property?"
          primaryButtonLabel="Delete"
          secondaryButtonLabel="Cancel"
          onPrimaryAction={() => handleDeleteProperty && handleDeleteProperty(listingData.id)}
          onSecondaryAction={() => setShowConfirmation && setShowConfirmation(null)}
          onClose={() => setShowConfirmation && setShowConfirmation(null)}
          isPrimaryDestructive={true}
          isLoading={isDeletingProperty}
          loadingLabel="Deleting..."
        />
      )}
    </>

  );
};

export default PropertyCardItem;